import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Loader2, Inbox } from "lucide-react";

export const CUR = "₪";
export const fmt = (n) => {
  const v = Number(n || 0);
  return `${CUR}${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};
export const fmt2 = (n) => `${CUR}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const pct = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(1)}%`);
export const todayISO = () => new Date().toISOString().slice(0, 10);

export const HEALTH_TONE = {
  "HIGH PROFIT": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  HEALTHY: "bg-teal-500/15 text-teal-300 border-teal-500/30",
  "LOW MARGIN": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  LOSS: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  UNKNOWN: "bg-white/5 text-white/50 border-[color:var(--border-subtle)]",
};

export const inputCls = "w-full rounded-lg bg-black/40 border border-[color:var(--border-subtle)] px-3 py-2.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]";
export const kicker = "text-[11px] uppercase tracking-[0.18em] text-gold-soft";
export const cardCls = "ascendia-card p-5";

// Simple data loader with reload + loading/error states.
export function useFinance(path, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try { const res = await api.get(path); setData(res.data); }
    catch (e) { console.error("finance load failed:", path, e); setError(true); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { load(); }, [load]);
  return { data, loading, error, reload: load, setData };
}

export async function downloadCsv(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); URL.revokeObjectURL(url);
}

export function Stat({ label, value, sub, tone = "text-white", testid }) {
  return (
    <div className="ascendia-card p-4" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wide text-muted-c">{label}</p>
      <p className={`mt-1.5 font-display text-2xl font-semibold tabular-nums ${tone}`}>{value}</p>
      {sub && <p className="mt-1 text-[11px] text-muted-c">{sub}</p>}
    </div>
  );
}

export function Segmented({ options, value, onChange, testid }) {
  return (
    <div className="inline-flex flex-wrap gap-1 rounded-xl border border-[color:var(--border-subtle)] p-1" data-testid={testid}>
      {options.map((o) => (
        <button key={o.k} onClick={() => onChange(o.k)} data-testid={`seg-${o.k}`}
          className={`rounded-lg px-3 py-1.5 text-xs transition-colors ${value === o.k ? "bg-gold text-black" : "text-white/70 hover:bg-white/5"}`}>{o.label}</button>
      ))}
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs text-secondary">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

export function Modal({ open, onClose, title, children, testid }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center" data-testid={testid}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-[color:var(--border-strong)] bg-[#141416] p-5 pb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold text-white">{title}</h3>
          <button onClick={onClose} aria-label="Close" className="h-8 w-8 rounded-lg border border-[color:var(--border-subtle)] flex items-center justify-center text-white/70 hover:bg-white/5"><X className="h-4 w-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Loading() {
  return <div className="flex items-center gap-2 text-muted-c py-10 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
}

export function EmptyState({ label = "No data yet" }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="finance-empty">
      <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-[color:var(--border-subtle)] bg-white/[0.02]"><Inbox className="h-5 w-5 text-muted-c" /></span>
      <p className="mt-3 text-sm text-muted-c">{label}</p>
    </div>
  );
}

export const money = (n) => fmt2(n);
